import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, TrendingDown, AlertTriangle, ShoppingCart, CheckCircle, Info } from "lucide-react";

const typeIcons = { price_change: TrendingDown, below_margin: AlertTriangle, out_of_stock: ShoppingCart, payment_failed: AlertTriangle };
const typeColors = { price_change: "text-amber-500", below_margin: "text-red-500", out_of_stock: "text-orange-500", payment_failed: "text-red-500" };

export default function AlertsPage() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const typeLabels = {
    price_change: t("alerts.type_price_change"),
    below_margin: t("alerts.type_below_margin"),
    out_of_stock: t("alerts.type_out_of_stock"),
    payment_failed: t("alerts.type_payment_failed"),
  };

  useEffect(() => {
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/alerts/${id}/read`);
      setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, read: true } : a));
    } catch { toast.error(t("common.error")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("alerts.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{alerts.filter((a) => !a.read).length} {t("alerts.unread")}</p>
        </div>
      </div>

      <Card className="bg-card border-border border-l-4 border-l-[#2CA8AC]" data-testid="alerts-info-card">
        <CardContent className="p-5 flex items-start gap-3">
          <Info className="w-5 h-5 text-[#2CA8AC] shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground leading-relaxed">
            <strong className="text-foreground">{t("alerts.info_title")}</strong> {t("alerts.info_desc")}
            <ul className="mt-3 space-y-1.5 list-disc pl-4">
              <li><strong>{t("alerts.type_price_change")}</strong> - {t("alerts.type_price_change_desc")}</li>
              <li><strong>{t("alerts.type_below_margin")}</strong> - {t("alerts.type_below_margin_desc")}</li>
              <li><strong>{t("alerts.type_out_of_stock")}</strong> - {t("alerts.type_out_of_stock_desc")}</li>
              <li><strong>{t("alerts.type_payment_failed")}</strong> - {t("alerts.type_payment_failed_desc")}</li>
            </ul>
            <p className="mt-3 text-xs">{t("alerts.no_alerts_desc")}</p>
          </div>
        </CardContent>
      </Card>

      {alerts.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t("alerts.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert) => {
            const Icon = typeIcons[alert.type] || Bell;
            const color = typeColors[alert.type] || "text-blue-500";
            return (
              <Card key={alert.id} className={`bg-card border-border transition-all ${!alert.read ? "border-l-4 border-l-blue-500" : "opacity-60"}`} data-testid={`alert-${alert.id}`}>
                <CardContent className="p-4 flex items-start gap-4">
                  <Icon className={`w-5 h-5 ${color} mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{alert.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">{typeLabels[alert.type] || alert.type}</Badge>
                      <span className="text-xs text-muted-foreground">{new Date(alert.created_at).toLocaleString("nl-NL")}</span>
                    </div>
                  </div>
                  {!alert.read && (
                    <Button variant="ghost" size="sm" onClick={() => markRead(alert.id)} data-testid={`mark-read-${alert.id}`}>
                      <CheckCircle className="w-4 h-4 mr-1" /> {t("alerts.mark_read")}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
